import React, { useEffect } from "react";
import {
  Grid,
  Paper,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import DynamicInputField from "../DynamicInputField";

const GeneralMasterForm = ({ general, setGeneral, loading, error }) => {
  useEffect(() => {
    if (general && general.length > 0) {
      const initializedGeneral = general.map((field) => {
        const fieldName = Object.keys(field).find(
          (key) => key !== "inputType" && key !== "options"
        );

        // Initialize values for specific measurement types if they are not initialized
        if (
          field.inputType === "1*2 measurements" &&
          (typeof field[fieldName] !== "object" || field[fieldName] === null)
        ) {
          return { ...field, [fieldName]: { x: "", y: "" } };
        }

        if (
          field.inputType === "1*3 measurements" &&
          (typeof field[fieldName] !== "object" || field[fieldName] === null)
        ) {
          return { ...field, [fieldName]: { x: "", y: "", z: "" } };
        }

        return field;
      });

      // Only update general if initialization is needed
      const isDifferent = initializedGeneral.some((field, index) => {
        const originalField = general[index];
        return JSON.stringify(field) !== JSON.stringify(originalField);
      });

      if (isDifferent) {
        setGeneral(initializedGeneral);
      }
    }
  }, [general, setGeneral]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    const updatedGeneral = general.map((field) => {
      const fieldName = Object.keys(field).find(
        (key) => key !== "inputType" && key !== "options"
      );

      // For measurement fields, handle nested changes (e.g., name="fieldName.x")
      if (
        field.inputType === "1*2 measurements" ||
        field.inputType === "1*3 measurements"
      ) {
        const [fieldKey, subKey] = name.split(".");
        if (fieldKey === fieldName) {
          return {
            ...field,
            [fieldName]: { ...field[fieldName], [subKey]: value },
          };
        }
      }

      if (fieldName === name) {
        return { ...field, [fieldName]: value };
      }
      return field;
    });

    setGeneral(updatedGeneral); // Update the general array
    // console.log(general);
  };

  const handleSelectChange = (name, value) => {
    const updatedGeneral = general.map((field) => {
      const fieldName = Object.keys(field).find(
        (key) => key !== "inputType" && key !== "options"
      );

      if (fieldName === name) {
        return { ...field, [fieldName]: value };
      }
      return field;
    });

    setGeneral(updatedGeneral);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={2}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" mt={2}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  if (!general || general.length === 0) {
    return (
      <Box display="flex" justifyContent="center" mt={2}>
        <Typography>No fields added</Typography>
      </Box>
    );
  }

  return (
    <Grid
      container
      item
      xs={12}
      justifyContent="center"
      style={{ marginTop: "2%" }}
    >
      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            {Array.isArray(general) &&
              general.map((field, index) => {
                // Ensure the inputType exists
                if (!field.inputType) {
                  console.error(
                    `Field at index ${index} does not have an inputType.`
                  );
                  return null; // Handle missing inputType appropriately
                }

                // Get the key (e.g., "New Field Name", "feildTest", etc.)
                const fieldName = Object.keys(field).find(
                  (key) => key !== "inputType" && key !== "options"
                );
                const fieldValue = field[fieldName] || "";

                return (
                  <TableRow key={index}>
                    <TableCell style={{ width: "30%" }}>{fieldName}</TableCell>
                    <TableCell style={{ width: "70%" }}>
                      <DynamicInputField
                        name={fieldName}
                        value={fieldValue} // Correctly passed value
                        inputType={field.inputType}
                        handleChange={handleChange}
                        handleSelectChange={handleSelectChange}
                        options={field.options || []} // Pass options or empty array
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </Grid>
  );
};

export default GeneralMasterForm;
